import React from 'react';

import {StorableItemType} from 'constants/ModelTypeConstants';
import {ParentStorableData, SelectedParentStorable} from 'constants/SampleConstants';
import {SampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {getParentStorableData} from 'samples/storables/ParentStorableUtil';
import SelectParentStorableModal from 'samples/storables/SelectParentStorableModal';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import ModalAnchor from 'ui-library/ModalAnchor';

interface ChangeParentStorableButtonProps {
  buttonStyle: ButtonStyle;
  buttonSize: ButtonSize;
  buttonText: string;
  className?: string;
  fullWidth?: boolean;
  icon?: BenchlingIconName;
  isWritable: boolean;
  onSetParentStorable: (
    data: ParentStorableData,
    eventGroupId: string,
    onFinish?: (errorMessage?: string) => void
  ) => void;
  /** If true, modal will wait for onFinish to be called from inside `onSetParentStorable` before closing.
   * If called with an error message, modal will remain up to render that error. Otherwise, it will close.
   * If false, modal will close immediately on submit and any errors will have to be surfaced via toasts. */
  waitForSubmitErrors?: boolean;
  parentStorableTypesOverride?: Array<StorableItemType>;
  storableSchema?: {id: string; registryId: string; name: string} | null;
  showRegistrySelector?: boolean;
  titleText: string;
  sampleStatusWarningParams?: Omit<SampleStatusWarningTrackingParams, 'eventGroupId'>;
  initialSelectedParentId?: string | null;
  allowNone?: boolean;
}

interface State {
  isParentStorableModalOpen: boolean;
}

export default class ChangeParentStorableButton extends React.Component<
  ChangeParentStorableButtonProps,
  State
> {
  static defaultProps = {
    buttonStyle: ButtonStyle.TERTIARY_BLUE,
    buttonSize: ButtonSize.SMALL,
    buttonText: 'Move',
    titleText: 'Move to...',
  };

  override state: State = {isParentStorableModalOpen: false};

  private handleOpenParentStorableModal = (): void => {
    this.setState({isParentStorableModalOpen: true});
  };

  private handleCloseParentStorableModal = (): void => {
    this.setState({isParentStorableModalOpen: false});
  };

  private handleOnSubmit = (
    selectedParent: SelectedParentStorable,
    eventGroupId: string,
    onFinish?: (errorMessage?: string) => void
  ): void => {
    const {onSetParentStorable} = this.props;
    onSetParentStorable(getParentStorableData(selectedParent), eventGroupId, onFinish);
  };

  override render(): JSX.Element {
    return (
      <div>
        <ModalAnchor open={this.state.isParentStorableModalOpen}>
          <SelectParentStorableModal
            storableSchema={this.props.storableSchema}
            showRegistrySelector={this.props.showRegistrySelector}
            modalTitleText={this.props.titleText}
            onRequestClose={this.handleCloseParentStorableModal}
            onSubmit={this.handleOnSubmit}
            parentStorableTypesOverride={this.props.parentStorableTypesOverride}
            submitText="Save"
            sampleStatusWarningParams={this.props.sampleStatusWarningParams}
            initialSelectedParentId={this.props.initialSelectedParentId}
            allowNone={this.props.allowNone}
            waitForSubmitErrors={this.props.waitForSubmitErrors}
          />
        </ModalAnchor>
        <Tooltip placement={TooltipPlacement.TOP} title={this.props.titleText}>
          <Button
            buttonStyle={this.props.buttonStyle}
            className={this.props.className}
            disabled={!this.props.isWritable}
            onClick={this.handleOpenParentStorableModal}
            size={this.props.buttonSize}
            text={this.props.buttonText}
            fullWidth={this.props.fullWidth}
            icon={this.props.icon}
            dataTestAttrs={{component: 'ChangeParentStorableButton', element: 'button'}}
          />
        </Tooltip>
      </div>
    );
  }
}
